var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from 'react';
import { css as emotionCss, cx } from 'emotion';
import { getChildrenByType } from 'react-nanny';
import { getAutomation, getDebug, getStyleNames } from '@citrus/runtime';
import { get, has, iif, isNotNullOrEmpty, isNullOrEmpty, removeAt } from '@pcty/utils';
import { useTable } from './private/TableContext';
import { editing as editingLegacy, trHighlight as trHighlightLegacy } from '../styles/legacy';
import { editing as editingCitron, trHighlight as trHighlightCitron } from '../styles/citron';
import { TdToggleDisabled, TdToggleExpanded, TdToggleNotExpanded, Tr as Row, TrAlt as AltRow, TrAltDisabled as AltDisabled, TrAltError as AltError, TrAltWarning as AltWarning, TrDisabled as Disabled, TrError as Error, TrWarning as Warning, } from './Table.styled';
import TableRowExpandable from './TableRowExpandable';
import TableRowTemplate from './TableRowTemplate';
import Td from './Td';
import { useTheme } from '@citrus/user';
var VERSION = '6.12.2';
// @private
// __checkHandler: () => (item: T) => void
// __expandableToggle?: ExpandableToggle;
// __onClick?: () => void;
var Tr = function (_a) {
    var _b;
    var _c = _a.alt, alt = _c === void 0 ? true : _c, automationId = _a.automationId, children = _a.children, className = _a.className, css = _a.css, _d = _a.disabled, disabled = _d === void 0 ? false : _d, _e = _a.editing, editing = _e === void 0 ? false : _e, _f = _a.error, error = _f === void 0 ? false : _f, _g = _a.highlight, highlight = _g === void 0 ? false : _g, _h = _a.warning, warning = _h === void 0 ? false : _h, _j = _a.__altByIndex, __altByIndex = _j === void 0 ? false : _j, __checked = _a.__checked, __checkHandler = _a.__checkHandler, __onClick = _a.__onClick, _k = _a.__expandableToggle, __expandableToggle = _k === void 0 ? '' : _k, _l = _a.__TYPE, TYPE = _l === void 0 ? 'Tr' : _l, other = __rest(_a, ["alt", "automationId", "children", "className", "css", "disabled", "editing", "error", "highlight", "warning", "__altByIndex", "__checked", "__checkHandler", "__onClick", "__expandableToggle", "__TYPE"]);
    var isCitron = useTheme().citrus2022;
    var editingCss = iif(isCitron, editingCitron, editingLegacy);
    var trHighlight = iif(isCitron, trHighlightCitron, trHighlightLegacy);
    var _m = useTable(), dividerIndexes = _m.dividerIndexes, hiddenColumns = _m.hiddenColumns;
    var cols = getChildrenByType(children, ['Td', 'TdCheckbox']);
    // This check is for Expandable rows which may have the Td's in a React Fragment or another Tr
    if (isNullOrEmpty(cols) && isNotNullOrEmpty(children) && has(children, 'props.children')) {
        cols = getChildrenByType(get(children, 'props.children'), ['Td', 'TdCheckbox']);
    }
    cols = removeAt.apply(void 0, __spreadArray([cols], hiddenColumns, false));
    if (isNotNullOrEmpty(cols)) {
        cols = cols.map(function (x, index) {
            var _a, _b, _c, _d;
            if (x.props.__TYPE === 'TdCheckbox') {
                return React.cloneElement(x, {
                    checked: __checked !== null && __checked !== void 0 ? __checked : x.props.checked,
                    disabled: disabled || editing || x.props.disabled,
                    key: isNotNullOrEmpty(automationId)
                        ? "data-table-tr-td-".concat(automationId, "-").concat(index)
                        : "data-table-tr-td-".concat(index),
                    onChange: __checkHandler !== null && __checkHandler !== void 0 ? __checkHandler : x.props.onChange,
                    divider: (_a = x.props.divider) !== null && _a !== void 0 ? _a : dividerIndexes.indexOf(index) >= 0,
                    dividerBefore: (_b = x.props.dividerBefore) !== null && _b !== void 0 ? _b : dividerIndexes.indexOf(index - 1) >= 0,
                });
            }
            return React.cloneElement(x, {
                disabled: disabled,
                divider: (_c = x.props.divider) !== null && _c !== void 0 ? _c : dividerIndexes.indexOf(index) >= 0,
                dividerBefore: (_d = x.props.dividerBefore) !== null && _d !== void 0 ? _d : dividerIndexes.indexOf(index - 1) >= 0,
            });
        });
    }
    var emotionObj = (_b = {},
        _b[emotionCss(editingCss)] = !disabled && editing,
        _b[emotionCss(trHighlight)] = !disabled && highlight,
        _b[emotionCss(css)] = isNotNullOrEmpty(css),
        _b);
    var classes = cx(emotionObj, className || '');
    var styleNames = {
        editingCss: emotionObj[emotionCss(editingCss)],
        trHighlight: emotionObj[emotionCss(trHighlight)],
    };
    var altRowColor = alt ? __altByIndex : alt;
    var Component = getComponent(altRowColor, error, warning, disabled);
    return (React.createElement(Component, __assign({ className: classes, styleNames: getStyleNames(styleNames) }, other, getAutomation(automationId), getDebug(sessionStorage.getItem('citrus-debug') === 'true', TYPE, VERSION)),
        renderExpandableToggle(__expandableToggle, disabled, __onClick),
        cols));
};
var getComponent = function (alt, error, warning, disabled) {
    if (disabled) {
        return alt ? AltDisabled : Disabled;
    }
    if (!disabled && error) {
        return alt ? AltError : Error;
    }
    if (!disabled && warning) {
        return alt ? AltWarning : Warning;
    }
    return alt ? AltRow : Row;
};
var renderExpandableToggle = function (expandableToggle, disabled, onClick) {
    if (disabled && (expandableToggle === 'expanded' || expandableToggle === 'not-expanded'))
        return React.createElement(TdToggleDisabled, null);
    switch (expandableToggle) {
        case 'buffer':
            return React.createElement(Td, null);
        case 'expanded':
            return React.createElement(TdToggleExpanded, { onClick: onClick });
        case 'not-expanded':
            return React.createElement(TdToggleNotExpanded, { onClick: onClick });
        default:
            return;
    }
};
Tr.defaultProps = {
    alt: true,
    disabled: false,
    editing: false,
    error: false,
    __altByIndex: false,
    __expandableToggle: '',
    __TYPE: 'Tr',
};
export default Object.assign(Tr, {
    Expandable: TableRowExpandable,
    Template: TableRowTemplate,
});
